import React from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import { Button, CorrectionBar } from '../../ui';

/**
 * Under the sheet: the "Add line" toolbar with a running count of unsaved
 * changes, the save refusal (when there is one), and the Robbie bar — the
 * completeness line in Robbie's voice, "Enhance with Robbie", Cancel, Save.
 */
export interface BomSheetFooterProps {
  readOnly: boolean;
  saving: boolean;
  /** Robbie's completeness line, already worded. */
  message: string;
  changedCount: number;
  addedCount: number;
  removedCount: number;
  hasChanges: boolean;
  invalidCount: number;
  /** Robbie's account of a refused save, already worded. */
  failure: string | null;
  onAddLine: () => void;
  onEnhance?: () => void;
  onCancel: () => void;
  onSave: () => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const BomSheetFooter: React.FC<BomSheetFooterProps> = ({
  readOnly,
  saving,
  message,
  changedCount,
  addedCount,
  removedCount,
  hasChanges,
  invalidCount,
  failure,
  onAddLine,
  onEnhance,
  onCancel,
  onSave,
}) => {
  const pending = [
    changedCount > 0 && `${changedCount} changed`,
    addedCount > 0 && `${addedCount} added`,
    removedCount > 0 && `${removedCount} to remove`,
  ].filter(Boolean);

  return (
    <>
      {!readOnly && (
        <div className="flex items-center gap-s4 border-t border-line-divider px-s6 py-s3">
          <Button variant="ghost" size="sm" iconLeft={<Plus />} onClick={onAddLine} disabled={saving}>
            Add line
          </Button>
          {pending.length > 0 && (
            <span className="font-mono text-meta text-ink-secondary">Unsaved: {pending.join(' · ')}</span>
          )}
          {invalidCount > 0 && (
            <span className="ml-auto text-meta text-danger-ink">
              {plural(invalidCount, 'cell')} to fix before I can save
            </span>
          )}
        </div>
      )}

      {failure && (
        <div role="alert" className="flex items-start gap-s3 border-t border-danger-border bg-danger-bg px-s6 py-s3">
          <AlertTriangle className="mt-[2px] h-[14px] w-[14px] shrink-0 text-danger-ink" />
          <p className="text-body-sm text-ink-body">{failure}</p>
        </div>
      )}

      <CorrectionBar
        message={message}
        actionLabel="Enhance with Robbie"
        onAction={readOnly ? undefined : onEnhance}
        actionDisabled={hasChanges || saving}
        actionHint={
          readOnly
            ? 'Only an editor of this RFQ can change its BOM'
            : hasChanges
              ? 'Save or cancel your edits first, so they are not lost'
              : undefined
        }
        secondaryLabel={readOnly ? 'Done' : 'Cancel'}
        onSecondary={onCancel}
      >
        {!readOnly && (
          <Button size="sm" className="whitespace-nowrap" onClick={onSave} disabled={!hasChanges || invalidCount > 0 || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </CorrectionBar>
    </>
  );
};

export default BomSheetFooter;
